import { Map as ImmutableMap } from "immutable";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { IUserOktaInfo, UserApi } from "../../api/UserApi";
import { ClientsAndPlans, IClientInfo, IPlanInfo } from "../../model/ClientsAndPlans.model";
import { getUserTypes, IUserInfo, UserInfo, UserTypeEnum } from "../../model/UserInfo.model";
import { prominentButtonStyle, secondaryButtonStyle } from "../common/buttonStyles";
import { RaisedButton } from "../common/RaisedButton";
import { byName } from "../../utils/listUtil";
import { ErrorComponent } from "../base/Error.component";
import { setHeaderNotification } from "../base/header/HeaderActions";
import { NotificationTypes } from "../base/header/HeaderReducer";
import { RequestSucceeded } from "../common/commonStates";
import { ISelectValue, SelectComponent } from "../common/Select.component";
import adminActions from "./adminActions";
import AdminAddPlanComponent from "./AdminAddPlan.component";
import { IAdminState } from "./AdminReducer";
import { capitalizeFirstLetter } from "../../utils/commonUtil";
import { LoadingSpinner } from "../icons/LoadingSpinner.component";
import { IApplicationRootState } from "../../../applicationState";
import {useNavigate} from "react-router-dom";
import {useParams} from "react-router";
import {navigateTo} from "../../../navigateTo";

export const MFA_SELECT_VALUES: ISelectValue[] = [
    { name: "MFA", id: 1 },
    { name: "Non MFA", id: 0 },
];

type IAdminUserEditComponentPropsFromReduxStore = IAdminState;

export interface IAdminUserEditActions {
    actions: {
        updateUserDetails: typeof adminActions.updateUserDetails;
        setHeaderNotification: typeof setHeaderNotification;
        updateUserMfa: typeof adminActions.updateUserMfa;
    };
}

export type AdminUserEditComponentProps =
    IAdminUserEditActions
    & IAdminUserEditComponentPropsFromReduxStore;

export const AdminUserEditComponent: React.FC<AdminUserEditComponentProps> = (props) => {
    const [loading, setLoading] = React.useState(true);
    const [isUserFound, setIsUserFound] = React.useState(false);
    const [userInfo, setUserInfo] = React.useState<IUserInfo>({} as UserInfo);
    const [originalUserInfo, setOriginalUserInfo] = React.useState<IUserInfo>({} as UserInfo);
    const [allPlans, setAllPlans] = React.useState<ImmutableMap<number, IPlanInfo>>(ImmutableMap());
    const [resendEmailClicked, setResendEmailClicked] = React.useState(false);
    const [resetPasswordClicked, setResetPasswordClicked] = React.useState(false);
    const [submitClicked, setSubmitClicked] = React.useState(false);
    const [userOktaInfo, setUserOktaInfo] = React.useState<IUserOktaInfo>({} as IUserOktaInfo);
    const [originalUserOktaInfo, setOriginalUserOktaInfo] = React.useState<IUserOktaInfo>({} as IUserOktaInfo);
    const [resetMfaFactorsClicked, setResetMfaFactorsClicked] = React.useState(false);

    const navigate = useNavigate();

    const { arbtSecurityIdParam } = useParams();
    const arbtSecurityId = arbtSecurityIdParam as string;

    React.useEffect(() => {
        const adminUsersInfoState = props.adminUsersInfoState as RequestSucceeded<IUserInfo[]>;
        const user = adminUsersInfoState.data.find(
            (x) => x.arbtSecurityId === arbtSecurityId
        );
        if (user) {
            UserApi.getUserOktaInfo(user.arbtSecurityId).then((oktaInfo) => {
                setLoading(false);
                setIsUserFound(true);
                setUserInfo(user);
                setOriginalUserInfo(user);
                setAllPlans(getAllPlans());
                setUserOktaInfo(oktaInfo);
                setOriginalUserOktaInfo(oktaInfo);
            });
        } else {
            setLoading(false);
            setIsUserFound(false);
        }
    }, [arbtSecurityIdParam]);

    function getAllPlans(): ImmutableMap<number, IPlanInfo> {
        const clientsAndPlans = (props.adminClientInfoState as RequestSucceeded<ClientsAndPlans>).data;
        return clientsAndPlans.reduce(
            (old: ImmutableMap<number, IPlanInfo>, current: IClientInfo) => old.merge(current.plans),
            ImmutableMap<number, IPlanInfo>()
        );
    }

    function getUserPlans() {
        return userInfo.planIds
            .map((planId: number) => allPlans.get(planId)!)
            .sort(byName);
    }

    function handleUserTypeChange(e: any) {
        setUserInfo({ ...userInfo, userType: e.target.value.toUpperCase() as UserTypeEnum });
    }

    function handleMfaChange(e: any) {
        setUserOktaInfo({ ...userOktaInfo, isMfa: Boolean(e.target.value) });
    }

    function handleApiResult(
        apiResult: Promise<any>,
        successMessage: string,
        failureMessage: string,
        finallyFunc: () => void
    ) {
        apiResult
            .then(() => {
                props.actions.setHeaderNotification(
                    { message: successMessage, notificationType: NotificationTypes.SUCCESS },
                    5000
                );
            })
            .catch(() => {
                props.actions.setHeaderNotification(
                    { message: failureMessage, notificationType: NotificationTypes.FAILURE },
                    5000
                );
            })
            .finally(finallyFunc);
    }

    function handleResetMfaFactors(arbtSecurityId: string) {
        if (resetMfaFactorsClicked) return;
        setResetMfaFactorsClicked(true);
        window.scrollTo(0, 0);
        const func = () => setResetMfaFactorsClicked(false);
        const api = UserApi.resetMfaFactors(arbtSecurityId);
        handleApiResult(api, "MFA factors have been reset successfully", "Unable to reset MFA factors", func);
    }

    function handleSendEmailClick(arbtSecurityId: string) {
        if (resendEmailClicked) return;
        setResendEmailClicked(true);
        window.scrollTo(0, 0);
        const func = () => setResendEmailClicked(false);
        const api = UserApi.sendRegistrationEmail(arbtSecurityId);
        handleApiResult(api, "Registration email successfully sent", "Error: Unable to send registration email", func);
    }

    function handleResetPassword(arbtSecurityId: string) {
        if (resetPasswordClicked) return;
        setResetPasswordClicked(true);
        window.scrollTo(0, 0);
        const func = () => setResetPasswordClicked(false);
        const api = UserApi.sendResetPasswordEmail(arbtSecurityId);
        handleApiResult(api, "Reset password email successfully sent", "Error: Unable to send reset password email", func);
    }

    function handleSave() {
        if (userInfo.planIds.length === 0) {
            setSubmitClicked(true);
            props.actions.setHeaderNotification(
                { message: "Please complete all required fields before saving", notificationType: NotificationTypes.FAILURE },
                5000
            );
            return;
        }
        props.actions.updateUserDetails(userInfo.arbtSecurityId, userInfo.userType, userInfo.planIds);
    }

    function onAddPlan(plan: IPlanInfo) {
        const planIds = userInfo.planIds.filter((planId) => planId !== plan.id);
        planIds.push(plan.id);
        setUserInfo({ ...userInfo, planIds });
        setSubmitClicked(false);
    }

    function onRemovePlan(plan: IPlanInfo) {
        setUserInfo({ ...userInfo, planIds: userInfo.planIds.filter((x) => x !== plan.id) });
    }

    function renderUserTypeDropDown(userType: string) {
        const userTypes: ISelectValue[] = getUserTypes();
        return (
            <SelectComponent
                id={"user-edit__user-type"}
                values={userTypes}
                selected={userType}
                width={250}
                submitClicked={false}
                handleChange={handleUserTypeChange}
                placeholder={"User Type"}
                renderRequired={false}
            />
        );
    }

    function renderMfaGroupDropDown(mfa: boolean) {
        return (
            <SelectComponent
                id={"user-edit__user-mfa"}
                values={MFA_SELECT_VALUES}
                selected={Number(mfa)}
                width={250}
                submitClicked={false}
                handleChange={handleMfaChange}
                placeholder={"User Type"}
                renderRequired={false}
            />
        );
    }

    function renderResetMfaFactors(arbtSecurityId: string) {
        const linkClassName = resetMfaFactorsClicked ? "clicked-link-14" : "clickable";
        return originalUserOktaInfo.isMfa ? (
            <div className="admin-user-edit__link-container">
                <a
                    className={`admin-user-edit__reset-mfa-link ${linkClassName}`}
                    onClick={() => handleResetMfaFactors(arbtSecurityId)}
                >
                    Reset MFA Factors
                </a>
            </div>
        ) : null;
    }

    function renderOktaLinks() {
        const linkClassName = resendEmailClicked ? "clicked-link-14" : "clickable";
        const linkPwdClassName = resetPasswordClicked ? "clicked-link-14" : "clickable";
        // const arbtSecurityId = props.match.params.arbtSecurityId;
        return (
            <div className="admin-user-edit__okta-links">
                <div className="admin-user-edit__link-container">
                    <a
                        className={`admin-user-edit__send-email-link ${linkClassName}`}
                        onClick={() => handleSendEmailClick(arbtSecurityId)}
                    >
                        Resend Welcome Email
                    </a>
                </div>
                <div className="admin-user-edit__link-container">
                    <a
                        className={`admin-user-edit__reset-password-link ${linkPwdClassName}`}
                        onClick={() => handleResetPassword(arbtSecurityId)}
                    >
                        Reset Password
                    </a>
                </div>
                {renderResetMfaFactors(arbtSecurityId)}
            </div>
        );
    }

    function renderMfaButtons() {
        return (
            <div className="admin-user-edit__mfa-buttons">
                <div className="save-and-cancel-buttons">
                    <RaisedButton
                        className="admin-user-edit__cancel-button"
                        style={secondaryButtonStyle}
                        primary={false}
                        onClick={() => {navigateTo("/admin", navigate);}}
                    >
                        Cancel
                    </RaisedButton>
                    <div className="buttons-spacer-between" />
                    <RaisedButton
                        className="admin-user-edit__save-button"
                        style={prominentButtonStyle}
                        primary={true}
                        onClick={() =>
                            props.actions.updateUserMfa(
                                userInfo.arbtSecurityId,
                                userOktaInfo.isMfa
                            )
                        }
                    >
                        Save Mfa
                    </RaisedButton>
                </div>
            </div>
        );
    }

    function renderAddPlanComponent() {
        return (
            <div>
                <h2>Add Plan(s)</h2>
                <AdminAddPlanComponent
                    onAddPlan={onAddPlan}
                    renderRequired={true}
                    submitClicked={submitClicked}
                />
            </div>
        );
    }

    function renderUserInfoSection() {
        const user = originalUserInfo!;
        const okta = originalUserOktaInfo;
        return (
            <div className="admin-user-edit__user-info" data-testid="admin-user-edit__user-info">
                <table className="admin-user-edit__user-info-table">
                    <tbody>
                    <tr className="admin-user-edit__user-table-row">
                        <td className="admin-user-edit__user-table-column admin-user-edit__user-info__first-name">
                            {user.firstName}
                        </td>
                        <td className="admin-user-edit__user-table-column admin-user-edit__user-info__last-name">
                            {user.lastName}
                        </td>
                        <td className="admin-user-edit__user-table-column admin-user-edit__user-info__email">
                            {user.email}
                        </td>
                        <td className="admin-user-edit__user-table-column admin-user-edit__user-info__type">
                            {user.userType.toString()}
                        </td>
                        <td className="admin-user-edit__user-table-column admin-user-edit__user-info__mfa">
                            {MFA_SELECT_VALUES.filter((x) => x.id === Number(okta.isMfa))[0].name}
                        </td>
                        <td className="admin-user-edit__user-table-column admin-user-edit__user-info__status">
                            {okta.status}
                        </td>
                        <td className="admin-user-edit__user-table-column admin-user-edit__user-info__last-login-date">
                            {user.lastLoginDate || "Never"}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    function renderIrisSettingsSection() {
        const user = userInfo!;
        const renderUserPlan = (x: IPlanInfo) => (
            <div className="admin-user-edit__user-plan" key={x.id}>
                <div className="remove-icon fas fa-x" onClick={() => onRemovePlan(x)} />
                <div>{x.name}</div>
                <br />
            </div>
        );
        return (
            <div className="admin-user-edit__user-plans">
                <h2>IRIS Settings</h2>
                <div className="admin-user-edit__label">User Type</div>
                {renderUserTypeDropDown(capitalizeFirstLetter(user.userType.toString()))}
                <h2>Current Plans</h2>
                {getUserPlans().map(renderUserPlan)}
            </div>
        );
    }

    function renderOktaSettingsSection() {
        return (
            <div className="admin-user-edit__user-okta-settings">
                <h2>OKTA Settings</h2>
                <div className="admin-user-edit__label">MFA Status</div>
                <div className="admin-user-edit__mfa">
                    {renderMfaGroupDropDown(userOktaInfo.isMfa)}
                    {renderMfaButtons()}
                </div>
                {renderOktaLinks()}
            </div>
        );
    }

    function renderButtons() {
        return (
            <div className="admin-user-edit__irs-settings-buttons">
                <div className="save-and-cancel-buttons">
                    <div className="buttons-spacer-left" />
                    <RaisedButton
                        className="admin-user-edit__cancel-button"
                        style={secondaryButtonStyle}
                        primary={false}
                        onClick={() => {navigateTo("/admin", navigate);}}
                    >
                        Cancel
                    </RaisedButton>
                    <div className="buttons-spacer-between" />
                    <RaisedButton
                        className="admin-user-edit__save-button"
                        style={prominentButtonStyle}
                        primary={true}
                        onClick={handleSave}
                    >
                        Save
                    </RaisedButton>
                </div>
                <div className="buttons-spacer-bottom" />
            </div>
        );
    }

    function renderAllInfo() {
        return (
            <div className="admin-user-edit">
                <h1>Editing User</h1>
                <h3>{`Okta Groups: ${userOktaInfo.groups}`}</h3>
                {renderUserInfoSection()}
                {renderIrisSettingsSection()}
                {renderAddPlanComponent()}
                {renderButtons()}
                {renderOktaSettingsSection()}
            </div>
        );
    }

    return loading ? <LoadingSpinner /> : isUserFound ? renderAllInfo() : <ErrorComponent />;
};

export const mapStateToProps = (state: IApplicationRootState): IAdminUserEditComponentPropsFromReduxStore => {
    return {
        ...state.adminState!,
    };
};

export const mapDispatchToProps = (dispatch: Dispatch): IAdminUserEditActions => {
    return {
        actions: bindActionCreators({
            updateUserDetails: adminActions.updateUserDetails,
            updateUserMfa: adminActions.updateUserMfa,
            setHeaderNotification,
        }, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminUserEditComponent);